// ABOUTME: What fills the panel header for the assistant — mark, name, disclaimer, chats toggle.
// ABOUTME: The frame, the expand and the close come from CModalCore, like every other panel.

import React from "react"
import { AIIcon } from "@/shared/icons/AIIcon"
import { HamburgerIcon } from "../icons"
import { useTranslation } from "react-i18next"
import { useDevice } from "@shared/hooks"
import type { UIText } from "../types"
import config from "@/config"

interface ChatHeaderProps {
  showConversationsList: boolean
  setShowConversationsList: (value: boolean) => void
  uiText: UIText
}

/**
 * The chats toggle is 34px on desktop and 40 on a phone — the assistant's own mock. That is off
 * the 30/36/44 control scale, which covers labelled controls; this is icon-only chrome, the same
 * case as the 34×30 rail items. Flagged in docs/DESIGN_SYSTEM.md.
 */
const HEAD_BTN = "flex size-8.5 flex-none items-center justify-center rounded-lg transition-colors max-md:size-10"

const ChatHeaderComponent: React.FC<ChatHeaderProps> = ({
  showConversationsList,
  setShowConversationsList,
  uiText,
}) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()

  return (
    <div className="flex w-full items-start gap-3 max-md:gap-2">
      {isMobile && (
        <button
          onClick={() => setShowConversationsList(!showConversationsList)}
          aria-label={t("chat.conversations")}
          className={`-ml-1.5 text-gray-600 hover:bg-secondary hover:text-gray-900 ${HEAD_BTN}`}
        >
          <HamburgerIcon className="size-4.5" />
        </button>
      )}

      {/* The mark sits in a white box on the tinted header, not on a grey chip. */}
      <span className="flex size-9.5 flex-none items-center justify-center overflow-hidden rounded-xl border border-border bg-white max-md:size-8">
        <AIIcon
          size={isMobile ? 24 : 28}
          bgClassName="bg-transparent"
        />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <h2 className="text-lg font-semibold tracking-[-0.01em] max-md:text-base">AI Assistant</h2>
        <p className="text-[13px] leading-snug text-muted-foreground">{uiText.disclaimer}</p>
        {!isMobile && (
          <p className="text-xs leading-snug text-pretty text-gray-400">
            {uiText.mcpInfo}{" "}
            <a
              href={config.MCP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {uiText.mcpLink}
            </a>
          </p>
        )}
      </div>
    </div>
  )
}

export const ChatHeader = React.memo(ChatHeaderComponent)
